import type { RouteLocationRaw } from 'vue-router';
import {
	PropertyTypeEnum,
	PropertyTypeSlug,
	propertyTypesSlugByValue,
	propertyTypesValueBySlug
} from '~/core/entities/CRMEnums';
import type { PropertyFilters } from '~/core/entities/property/filters';
import type { EmptyObject } from '~~/type-utils';
import { slugify } from '~~/utils';

export type TransactionTypeSlug = 'venda' | 'aluguel';

const transactionTypeSlugsByValue: Record<
	PropertyFilters['transactionType'],
	TransactionTypeSlug
> = {
	sell: 'venda',
	rent: 'aluguel'
} as const;

const queryStringFilters = [
	'transactionType',
	'searchTerm',
	'locationSlug',
	'isExclusive',
	'minArea',
	'maxArea',
	'minAskingPrice',
	'maxAskingPrice',
	'minRentPrice',
	'maxRentPrice',
	'minSquareMeterPrice',
	'maxSquareMeterPrice',
	'minCondoFee',
	'maxCondoFee',
	'minTax',
	'maxTax',
	'suites',
	'bedrooms',
	'bathrooms',
	'parkingSpots',
	'features',
	'propertyTypes',
	'commercialIds',
	'regions',
	'cities',
	'states',
	'macroregions',
	'sortBy',
	'orderBy',
	'pluscodesGroup'
] as const;

type QueryStringPropertyFilter = (typeof queryStringFilters)[number];
export type PageName = 'imoveis' | 'mapa-de-imoveis' | 'colecao';

export class PropertyFiltersPresenter {
	static DEFAULT_URL_PATH = '/venda/imoveis/sao-paulo-sp-brasil';
	static DEFAULT_MAP_URL_PATH = '/venda/mapa-de-imoveis/sao-paulo-sp-brasil';

	static #stringArrayFilters = [
		'features',
		'propertyTypes',
		'commercialIds',
		'regions',
		'cities',
		'states',
		'macroregions',
		'pluscodesGroup'
	] as const;

	static #numericFilters = [
		'minArea',
		'maxArea',
		'minAskingPrice',
		'maxAskingPrice',
		'minRentPrice',
		'maxRentPrice',
		'minSquareMeterPrice',
		'maxSquareMeterPrice',
		'minCondoFee',
		'maxCondoFee',
		'minTax',
		'maxTax',
		'suites',
		'bedrooms',
		'bathrooms',
		'parkingSpots'
	] as const;

	static #booleanFilters = ['isExclusive', 'onlyPluscodes'] as const;

	static #stringFilters = [
		'transactionType',
		'searchTerm',
		'locationSlug',
		'pluscodesGroup',
		'sortBy',
		'orderBy'
	] as const;

	static #getLocations({
		states,
		cities,
		regions
	}: Pick<PropertyFilters, 'states' | 'cities' | 'regions'>): {
		state: string;
		city: string;
		firstRegion: string;
		remainingRegions: string[];
	} {
		const state = states?.[0] || '';
		const city = state ? cities?.[0] || '' : '';

		const [firstRegion = '', ...remainingRegions] =
			city && regions ? regions : [];

		return { city, state, firstRegion, remainingRegions };
	}

	/**
	 * @param filters Filters to be converted to a path
	 *
	 * When defining a location filter, the less specific location
	 * should be present in order for the most specific location
	 * to be used; For example, while filtering by a city name,
	 * the state name should also be defined, and while filtering
	 * by a region name, the city and state names should also be defined.
	 * @param extraQueryStrings Extra query strings to be added to the path
	 * @param page Page name to be used in the path
	 * @returns Path string containing the filters
	 */
	static getPathFromFilters(
		{
			transactionType,
			propertyTypes,
			states,
			cities,
			regions,
			locationSlug: locationSlugFilter,
			...otherFilters
		}: Partial<PropertyFilters>,
		extraQueryStrings: Record<string, any> = {},
		{
			page = 'imoveis',
			collection
		}: {
			page?: PageName;
			collection?: string;
		} = {}
	): string {
		const self = PropertyFiltersPresenter;

		const transactionTypeSlug =
			self.getSlugFromTransactionType(transactionType);

		const { state, city, firstRegion, remainingRegions } = self.#getLocations({
			states,
			cities,
			regions
		});

		const defaultLocationSlug = 'sao-paulo-sp-brasil';

		const locationSlugParts = [firstRegion, city, state]
			.filter(Boolean)
			.map(slugify);

		const locationSlug = locationSlugParts.join('')
			? [...locationSlugParts, 'brasil'].join('-')
			: locationSlugFilter || defaultLocationSlug;

		const [firstPropertyType = '', ...remainingPropertyTypes] =
			propertyTypes || [];

		const propertyTypeSlug = firstPropertyType
			? propertyTypesSlugByValue[firstPropertyType]
			: null;

		const queryStringObject = new URLSearchParams();

		if ('page' in otherFilters) delete otherFilters.page;
		if ('perPage' in otherFilters) delete otherFilters.perPage;

		const queryStringFiltersObject = {
			...otherFilters,
			regions: remainingRegions,
			propertyTypes: remainingPropertyTypes.map(
				propertyType => propertyTypesSlugByValue[propertyType]
			)
		};

		const allQueryStrings = {
			...queryStringFiltersObject,
			...extraQueryStrings
		};

		for (const [key, value] of Object.entries(allQueryStrings)) {
			if (Array.isArray(value)) {
				if (value.length > 0) {
					queryStringObject.set(key, value.join(','));
				}

				continue;
			}

			if (value) queryStringObject.set(key, value.toString());
		}

		const queryString = queryStringObject.toString()
			? `?${queryStringObject.toString()}`
			: '';

		if (page === 'colecao') {
			return `/${page}/${collection}${queryString}`;
		}

		return propertyTypeSlug
			? `/${transactionTypeSlug}/${page}/${locationSlug}/${propertyTypeSlug}${queryString}`
			: `/${transactionTypeSlug}/${page}/${locationSlug}${queryString}`;
	}

	/**
	 * @param filters Filters to be converted to a path
	 *
	 * When defining a location filter, the less specific location
	 * should be present in order for the most specific location
	 * to be used; For example, while filtering by a city name,
	 * the state name should also be defined, and while filtering
	 * by a region name, the city and state names should also be defined.
	 * @param options.page Page name to be used in the path
	 * @returns {RouteLocationRaw} Route object containing path and query values that represent the filters
	 */
	static getRouteObjectFromFilters(
		filters: Partial<PropertyFilters>,
		{ page = 'imoveis' }: { page?: PageName } = {}
	): RouteLocationRaw {
		const fullPath = PropertyFiltersPresenter.getPathFromFilters(
			filters,
			{},
			{ page }
		);

		const [path, queryString] = fullPath.split('?');

		if (!queryString) return { path };

		const queryObject = Object.fromEntries(
			new URLSearchParams(queryString).entries()
		);

		return {
			path,
			query: queryObject
		};
	}

	static getFiltersFromPath(
		path: string,
		previousFilters?: PropertyFilters,
		page: PageName = 'imoveis'
	): PropertyFilters {
		const pathPrefixRegex =
			page === 'colecao'
				? new RegExp(`^/colecao/[a-z-]+/?`)
				: new RegExp(`^/(venda|aluguel)/${page}/?`);

		const isValidPropertiesFiltersPath = pathPrefixRegex.test(path);

		if (!isValidPropertiesFiltersPath) return { transactionType: 'sell' };

		const [transactionTypeSlug, ...locationAndPropertyTypeSlugWithQueryString] =
			path.replace(/^\//, '').split(`/${page}/`) as [
				TransactionTypeSlug?,
				string?
			];

		if (!transactionTypeSlug) return { transactionType: 'sell' };

		const [locationAndPropertyTypeSlugsString, queryString] =
			locationAndPropertyTypeSlugWithQueryString.join().split('?') || ['', ''];

		const [locationSlug, propertyTypeSlug = null] =
			locationAndPropertyTypeSlugsString?.split('/') || [null, null];

		const queryStringObject = new URLSearchParams(queryString);

		const queryStringPropertyTypes =
			queryStringObject.get('propertyTypes')?.split(',') || [];

		const allPropertyTypes = [propertyTypeSlug, ...queryStringPropertyTypes]
			.filter(
				slug =>
					slug !== null &&
					PropertyFiltersPresenter.#isAValidPropertyTypeSlug(slug)
			)
			.map(slug => propertyTypesValueBySlug[slug]);

		const queryStringFilters =
			PropertyFiltersPresenter.#getFiltersFromQueryString(queryString || '');

		if (previousFilters?.regions) {
			const firstRegion = slugify(previousFilters.regions[0] || '');

			if (locationSlug?.startsWith(firstRegion)) {
				queryStringFilters.regions = previousFilters.regions;
				queryStringFilters.cities = previousFilters.cities;
				queryStringFilters.states = previousFilters.states;
			}
		}

		if (previousFilters && previousFilters?.regions) {
			const firstRegion = slugify(previousFilters.regions[0] || '');

			if (locationSlug?.includes(firstRegion)) {
				queryStringFilters.regions = previousFilters.regions;
				queryStringFilters.cities = previousFilters.cities;
				queryStringFilters.states = previousFilters.states;
			}
		}

		return {
			...queryStringFilters,
			propertyTypes: allPropertyTypes,
			transactionType:
				PropertyFiltersPresenter.getTransactionTypeFromSlug(
					transactionTypeSlug
				),
			locationSlug: locationSlug || 'sao-paulo-sp-brasil'
		};
	}

	static #getFiltersFromQueryString(
		queryString: string
	): Record<QueryStringPropertyFilter, any> | EmptyObject {
		if (!queryString.trim()) return {};

		const queryStringObject = new URLSearchParams(queryString);

		const filters: Record<string, any> = {};

		for (const key of queryStringObject.keys()) {
			const valuesForCurrentKey = queryStringObject.getAll(key);

			if (!PropertyFiltersPresenter.#isQueryStringFilter(key)) continue;

			if (PropertyFiltersPresenter.#isStringArrayFilter(key)) {
				const values: string[] = valuesForCurrentKey
					.flatMap(value => value.split(','))
					.filter(value => value.trim());

				if (values.length > 0) {
					filters[key] = values;
				}

				continue;
			}

			if (PropertyFiltersPresenter.#isNumericFilter(key)) {
				const [firstNumericValue] = valuesForCurrentKey
					.map(Number)
					.filter(number => !Number.isNaN(number));

				if (typeof firstNumericValue === 'number') {
					filters[key] = firstNumericValue;
				}

				continue;
			}

			if (PropertyFiltersPresenter.#isBooleanFilter(key)) {
				const [firstBooleanValue] = valuesForCurrentKey
					.map(value => {
						if (value === 'true') return true;
						if (value === 'false') return false;

						return null;
					})
					.filter(value => value !== null);

				if (typeof firstBooleanValue === 'boolean') {
					filters[key] = firstBooleanValue;
				}

				continue;
			}

			if (PropertyFiltersPresenter.#isStringFilter(key)) {
				const [firstStringValue] = valuesForCurrentKey;

				if (firstStringValue) {
					filters[key] = firstStringValue;
				}

				continue;
			}
		}

		return filters;
	}

	static #isStringArrayFilter(key: string): boolean {
		return PropertyFiltersPresenter.#stringArrayFilters.includes(key as any);
	}

	static #isNumericFilter(key: string): boolean {
		return PropertyFiltersPresenter.#numericFilters.includes(key as any);
	}

	static #isBooleanFilter(key: string): boolean {
		return PropertyFiltersPresenter.#booleanFilters.includes(key as any);
	}

	static #isStringFilter(key: string): boolean {
		return PropertyFiltersPresenter.#stringFilters.includes(key as any);
	}

	static #isQueryStringFilter(key: string): key is QueryStringPropertyFilter {
		return queryStringFilters.includes(key as QueryStringPropertyFilter);
	}

	static #isAValidPropertyTypeSlug(slug: string): slug is PropertyTypeSlug {
		return slug in propertyTypesValueBySlug;
	}

	static getPropertyTypeFromSlug(
		slug: PropertyTypeSlug | string | null | undefined
	): PropertyTypeEnum | null {
		if (!slug) return null;
		if (!PropertyFiltersPresenter.#isAValidPropertyTypeSlug(slug)) return null;

		return propertyTypesValueBySlug[slug];
	}

	static #transactionTypesBySlug: Record<
		TransactionTypeSlug,
		PropertyFilters['transactionType']
	> = {
		venda: 'sell',
		aluguel: 'rent'
	} as const;

	static getSlugFromTransactionType(
		transactionType: PropertyFilters['transactionType'] | null | undefined
	): TransactionTypeSlug {
		return transactionTypeSlugsByValue[transactionType || 'sell'] || 'venda';
	}

	static getTransactionTypeFromSlug(
		slug: TransactionTypeSlug | null | undefined
	): PropertyFilters['transactionType'] {
		if (!slug) return 'rent';

		return PropertyFiltersPresenter.#transactionTypesBySlug[slug] || 'sell';
	}
	/**
	 * Retrieves the keys of filters that are considered applied based on their type and value.
	 *
	 * This method iterates over the keys of the provided `filters` object and checks each key's value
	 * to determine if it represents an applied filter. The type of the filter determines the criteria:
	 *
	 * - **Boolean filters**: The value must be `true`.
	 * - **String array filters**: The array must have a length greater than 0.
	 * - **Numeric filters**: The value must be greater than 0.
	 * - **String filters**: The string must have a length greater than 0.
	 *
	 * @param filters - An object containing filter properties and their values.
	 * @returns An array of keys for the filters that are applied.
	 *
	 * */
	static getAppliedFilterKeys(
		filters: PropertyFilters
	): (keyof PropertyFilters)[] {
		const appliedFilterKeys: (keyof PropertyFilters)[] = [];

		for (const key of Object.keys(filters) as (keyof PropertyFilters)[]) {
			if (
				PropertyFiltersPresenter.#isBooleanFilter(key) &&
				filters[key as keyof PropertyFilters]
			) {
				appliedFilterKeys.push(key);
				continue;
			}

			if (
				PropertyFiltersPresenter.#isStringArrayFilter(key) &&
				(filters[key as keyof PropertyFilters] as string[]).length > 0
			) {
				appliedFilterKeys.push(key);
				continue;
			}

			if (
				PropertyFiltersPresenter.#isNumericFilter(key) &&
				(filters[key as keyof PropertyFilters] as number) > 0
			) {
				appliedFilterKeys.push(key);
				continue;
			}

			if (
				PropertyFiltersPresenter.#isStringFilter(key) &&
				(filters[key as keyof PropertyFilters] as string).length > 0
			) {
				appliedFilterKeys.push(key);
				continue;
			}

			continue;
		}

		return appliedFilterKeys;
	}
}
